import React, { useRef, useEffect, useState } from "react";
import styles from "./AddEmployeePanel.module.css";
import {
  FiUser, FiMail, FiPhone,
  FiMapPin, FiHome, FiGlobe, FiCreditCard, FiKey, FiHash
} from "react-icons/fi";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import WebService from "../Services/WebService";
import { toast } from "react-toastify";
import DocUploadPopup from "./DocUploadPopup";
import { Employee } from "../Models/Models";
import CrossButton from "../Customs/Button/CrossButton";

interface Country { id: number; name: string; }
interface State { id: number; name: string; }
interface City { id: number; name: string; }

interface AddEmployeePanelProps {
  onClose: () => void;
  onSuccess?: () => void;
  editEmployee?: Employee | null;
}

const initialForm = {
  department_id: 2,
  username: "",
  email: "",
  mobile_number: "",
  password: "",
  user_status: "ACTIVE",
  role_id: "",
  user_type: "EMPLOYEE",
  joining_date: new Date().toISOString(),
  pan: "",
  account_number: "",
  address_type: "GENERAL",
  address: "",
  landmark: "",
  city: "",
  state: "",
  country: "",
};

const validate = (form: typeof initialForm, isEdit: boolean) => {
  const errors: Record<string, string> = {};
  if (!form.username.trim()) errors.username = "Name is required";
  if (!form.mobile_number) errors.mobile_number = "Contact number is required";
  else if (!/^\d{10}$/.test(form.mobile_number)) errors.mobile_number = "Contact must be 10 digits";
if (form.email && !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(form.email)) errors.email = "Invalid email format";

  if (!form.role_id) errors.role_id = "Role is required";
  if (!form.address) errors.address = "Address is required";
  if (!form.landmark) errors.landmark = "Landmark is required";
  if (!form.city) errors.city = "City is required";
  if (!form.state) errors.state = "State is required";
  if (!form.country) errors.country = "Country is required";
  if (!form.pan) errors.pan = "PAN is required";
  else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(form.pan.toUpperCase())) errors.pan = "Invalid PAN format (ABCDE1234F)";
  if (!isEdit) {
    if (!form.password) errors.password = "Password is required";
    else if (form.password.length < 6) errors.password = "Min 6 characters required";
  }
  return errors;
};

const AddEmployeePanel: React.FC<AddEmployeePanelProps> = ({
  onClose,
  onSuccess,
  editEmployee = null,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);

  const [roles, setRoles] = useState<{ role_id: number; role_name: string }[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  // Country/State/City
  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  // Password visibility
  const [showPassword, setShowPassword] = useState(false);
  
  // Document popup state
  const [showDocPopup, setShowDocPopup] = useState(false);
  const [newEmployeeId, setNewEmployeeId] = useState<number | null>(null);

useEffect(() => {
  setRolesLoading(true);
  WebService.getAPI<any>({ action: "/v1/door-management/rbac/roles", type: "" })
    .then((res) => {
      const fetchedRoles = Array.isArray(res.data) ? res.data : res.data?.roles || [];
      setRoles(fetchedRoles);
      // Do NOT auto-select role!
    })
    .finally(() => setRolesLoading(false));
}, [editEmployee]);



  // Prefill for edit
  useEffect(() => {
    if (editEmployee) {
      type EmployeeAddress = {
        address_type?: string;
        address?: string;
        landmark?: string;
        city?: number | string;
        state?: number | string;
        country?: number | string;
      };
      const addr: EmployeeAddress =
        Array.isArray(editEmployee.address) && editEmployee.address.length > 0
          ? editEmployee.address[0]
          : {};
      setForm({
        department_id: typeof editEmployee.department_id === "number"
          ? editEmployee.department_id
          : Number(editEmployee.department_id) || 2,
        username: editEmployee.username ?? "",
        email: editEmployee.email ?? "",
        mobile_number: editEmployee.mobile_number ?? "",
        password: "",
        user_status: editEmployee.user_status ?? "ACTIVE",
        role_id: editEmployee.role_id ? String(editEmployee.role_id) : "",
        user_type: editEmployee.user_type ?? "EMPLOYEE",
        joining_date: editEmployee.joining_date ?? new Date().toISOString(),
        pan: editEmployee.pan ?? "",
        account_number: editEmployee.account_number ?? "",
        address_type: addr.address_type || "GENERAL",
        address: addr.address || "",
        landmark: addr.landmark || "",
        city: addr.city ? String(addr.city) : "",
        state: addr.state ? String(addr.state) : "",
        country: addr.country ? String(addr.country) : "",
      });
    } else {
      setForm(initialForm);
    }
    setShowPassword(false);
  }, [editEmployee]);

  // Fetch countries on mount
  useEffect(() => {
    setLoadingCountries(true);
    WebService.getAPI<any>({ action: "/v1/door-management/users/countries", type: "" })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : res.data?.countries || [];
        setCountries(data);
        setLoadingCountries(false);
      })
      .catch(() => {
        setCountries([]);
        setLoadingCountries(false);
      });
  }, []);

  // Fetch states when country changes
  useEffect(() => {
    if (!form.country) { setStates([]); setCities([]); return; }
    setLoadingStates(true);
    WebService.getAPI<any>({ action: `/v1/door-management/users/countries/${form.country}/states`, type: "" })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : res.data?.data || res.data?.states || [];
        setStates(data);
        setLoadingStates(false);
      })
      .catch(() => {
        setStates([]);
        setLoadingStates(false);
      });
  }, [form.country]);

  // Fetch cities when state changes
  useEffect(() => {
    if (!form.state) { setCities([]); return; }
    setLoadingCities(true);
    WebService.getAPI<any>({ action: `/v1/door-management/users/states/${form.state}/cities`, type: "" })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : res.data?.data || res.data?.cities || [];
        setCities(data);
        setLoadingCities(false);
      })
      .catch(() => {
        setCities([]);
        setLoadingCities(false);
      });
  }, [form.state]);


// Close on click outside + Esc key
useEffect(() => {
  const handleClick = (event: MouseEvent) => {
    if (
      !showDocPopup &&
      panelRef.current &&
      !panelRef.current.contains(event.target as Node)
    ) {
      onClose();
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape" && !showDocPopup) {
      onClose();
    }
  };

  document.addEventListener("mousedown", handleClick);
  document.addEventListener("keydown", handleKeyDown);

  return () => {
    document.removeEventListener("mousedown", handleClick);
    document.removeEventListener("keydown", handleKeyDown);
  };
}, [onClose, showDocPopup]);


  // Handle field changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === "mobile_number") {
      const clean = value.replace(/\D/g, "").slice(0, 10);
      setForm((prev) => ({ ...prev, [name]: clean }));
      setErrors((prev) => ({ ...prev, mobile_number: "" }));
    } else if (name === "pan") {
      setForm((prev) => ({ ...prev, [name]: value.toUpperCase() }));
      setErrors((prev) => ({ ...prev, pan: "" }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
      if (name === "country") setForm((prev) => ({ ...prev, state: "", city: "" }));
      if (name === "state") setForm((prev) => ({ ...prev, city: "" }));
    }
  };

  // Submit
   // --- Submit Handler ---
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({}); // clear previous
    setLoading(true);

    const validationErrors = validate(form, !!editEmployee);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setLoading(false);
      return;
    }

    const body = {
      department_id: form.department_id,
      username: form.username,
      email: form.email,
      mobile_number: form.mobile_number,
      password: form.password,
      user_status: form.user_status,
      role_id: +form.role_id,
      user_type: form.user_type,
      joining_date: form.joining_date,
      pan: form.pan,
      account_number: form.account_number,
      address: {
        address_type: form.address_type,
        address: form.address,
        landmark: form.landmark,
        city: +form.city,
        state: +form.state,
        country: +form.country,
      },
    };

    try {
      if (editEmployee) {
        await WebService.putAPI({
          action: `/v1/door-management/employees/${editEmployee.user_id}`,
          body,
        });
        toast.success("Employee updated successfully!");
        setLoading(false);
        if (onSuccess) onSuccess();
        onClose();
      } else {
        const res = await WebService.postAPI<{
          data?: { employee?: { user_id?: number }, employee_id?: number, user_id?: number }
        }>({ action: "/v1/door-management/employees", body });

        const employeeId =
          res?.data?.employee?.user_id || res?.data?.employee_id || res?.data?.user_id;
        if (employeeId) {
          setNewEmployeeId(employeeId);
          setShowDocPopup(true);
        } else {
          if (onSuccess) onSuccess();
          onClose();
        }
        setForm(initialForm);
        setLoading(false);
      }
    } catch (err: any) {
  setLoading(false);

  // If error response has data, extract details
  if (err?.response?.data) {
    const data = err.response.data;

    // Backend returns { message: "...", errors: { field: "message" } }
    if (typeof data === "object") {
      let fieldErrors: Record<string, string> = {};
      if (data.errors && typeof data.errors === "object") {
        fieldErrors = { ...data.errors };
      }
      // Also handle single field errors at the top-level
      if (data.message) fieldErrors.form = data.message;

      setErrors(fieldErrors);
      return;
    }

    // If message as string
    if (typeof data === "string") {
      setErrors({ form: data });
      return;
    }
  }

  // Fallback to generic error
  setErrors({ form: typeof err === "string" ? err : "Failed to add/update employee" });
}

  };

  const handleTogglePassword = () => setShowPassword((prev) => !prev);

  // Popup close
  const handleDocPopupClose = () => {
    setShowDocPopup(false);
    setNewEmployeeId(null);
    if (onSuccess) onSuccess();
    onClose();
  };

  return (
    <>
      <div className={styles.overlay}>
        <div className={styles.panel} ref={panelRef}>
<CrossButton onClick={onClose} className={styles.closeBtn} />

          <h2 className={styles.heading}>
            {editEmployee ? "Edit Employee" : "Add New Employee"}
          </h2>
          <form className={styles.form} onSubmit={handleSubmit} autoComplete="off">
            {/* Name & Contact */}
            <div className={styles.row}>
              <div className={styles.inputGroup}>
                <label>Employee Name</label>
                <div className={styles.inputWithIcon}>
                  <FiUser className={styles.icon} />
                  <input
                    type="text"
                    name="username"
                    placeholder="Employee Name"
                    value={form.username}
                    onChange={handleChange}
                    // NO required!
                    style={errors.username ? { borderColor: "#e43e2d" } : {}}
                  />
                </div>
                {errors.username && <div className={styles.errorMsg}>{errors.username}</div>}
              </div>
              <div className={styles.inputGroup}>
                <label>Contact Number</label>
                <div className={styles.inputWithIcon}>
                  <FiPhone className={styles.icon} />
                  <input
                    type="text"
                    name="mobile_number"
                    placeholder="e.g.9876543210"
                    value={form.mobile_number}
                    onChange={handleChange}
                    maxLength={10}
                    inputMode="numeric"
                    style={errors.mobile_number ? { borderColor: "#e43e2d" } : {}}
                  />
                </div>
                {errors.mobile_number && <div className={styles.errorMsg}>{errors.mobile_number}</div>}
              </div>
            </div>
            {/* Email & Role */}
         <div className={styles.row}>
  {/* Email input */}
  <div className={styles.inputGroup}>
    <label>Email Address (Optional)</label>
    <div className={styles.inputWithIcon}>
      <FiMail className={styles.icon} />
      <input
        type="email"
        name="email"
        placeholder="youremail@gmail.com"
        value={form.email}
        onChange={handleChange}
        // style={errors.email ? { borderColor: "#e43e2d" } : {}}
      />
    </div>
    {/* {errors.email && <div className={styles.errorMsg}>{errors.email}</div>} */}
  </div>
  {/* Assign Role dropdown */}
  <div className={styles.inputGroup}>
    <label>Assign Role</label>
    <div className={styles.inputWithIcon}>
      <FiUser className={styles.icon} />
      <select
        name="role_id"
        value={form.role_id}
        onChange={handleChange}
        disabled={rolesLoading || roles.length === 0}
        style={errors.role_id ? { borderColor: "#e43e2d" } : {}}
      >
        <option value="">Select Role</option>
        {rolesLoading && <option>Loading...</option>}
        {!rolesLoading && roles.length === 0 && (
          <option>No roles found</option>
        )}
        {!rolesLoading &&
          roles.map((role) => (
            <option key={role.role_id} value={role.role_id}>
              {role.role_name}
            </option>
          ))}
      </select>
      {/* <FiChevronDown className={styles.chevron} /> */}
    </div>
    {errors.role_id && <div className={styles.errorMsg}>{errors.role_id}</div>}
  </div>
</div>

            {/* Address */}
            <div className={styles.inputGroup}>
              <label>Address</label>
              <div className={styles.inputWithIcon}>
                <FiMapPin className={styles.icon} />
                <input
                  type="text"
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  placeholder="e.g. Street Address"
                  style={errors.address ? { borderColor: "#e43e2d" } : {}}
                />
              </div>
              {errors.address && <div className={styles.errorMsg}>{errors.address}</div>}
            </div>
            {/* Landmark, Country */}
            <div className={styles.row}>
              <div className={styles.inputGroup}>
                <label>Landmark</label>
                <div className={styles.inputWithIcon}>
                  <FiHome className={styles.icon} />
                  <input
                    type="text"
                    name="landmark"
                    value={form.landmark}
                    onChange={handleChange}
                    placeholder="e.g. Near City Mall"
                    style={errors.landmark ? { borderColor: "#e43e2d" } : {}}
                  />
                </div>
                {errors.landmark && <div className={styles.errorMsg}>{errors.landmark}</div>}
              </div>
              <div className={styles.inputGroup}>
                <label>Country</label>
                <div className={styles.inputWithIcon}>
                  <FiGlobe className={styles.icon} />
                  <select
                    name="country"
                    value={form.country}
                    onChange={handleChange}
                    style={errors.country ? { borderColor: "#e43e2d" } : {}}
                  >
                    <option value="">Select Country</option>
                    {loadingCountries ? (
                      <option>Loading...</option>
                    ) : (
                      countries.map((country) => (
                        <option key={country.id} value={country.id}>
                          {country.name}
                        </option>
                      ))
                    )}
                  </select>
                </div>
                {errors.country && <div className={styles.errorMsg}>{errors.country}</div>}
              </div>
            </div>
            {/* State, City */}
            <div className={styles.row}>
              <div className={styles.inputGroup}>
                <label>State</label>
                <div className={styles.inputWithIcon}>
                  <FiMapPin className={styles.icon} />
                  <select
                    name="state"
                    value={form.state}
                    onChange={handleChange}
                    disabled={!form.country || loadingStates || states.length === 0}
                    style={errors.state ? { borderColor: "#e43e2d" } : {}}
                  >
                    <option value="">Select State</option>
                    {loadingStates ? (
                      <option>Loading...</option>
                    ) : (
                      states.map((state) => (
                        <option key={state.id} value={state.id}>
                          {state.name}
                        </option>
                      ))
                    )}
                  </select>
                </div>
                {errors.state && <div className={styles.errorMsg}>{errors.state}</div>}
              </div>
              <div className={styles.inputGroup}>
                <label>City</label>
                <div className={styles.inputWithIcon}>
                  <FiMapPin className={styles.icon} />
                  <select
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    disabled={!form.state || loadingCities || cities.length === 0}
                    style={errors.city ? { borderColor: "#e43e2d" } : {}}
                  >
                    <option value="">Select City</option>
                    {loadingCities ? (
                      <option>Loading...</option>
                    ) : (
                      cities.map((city) => (
                        <option key={city.id} value={city.id}>
                          {city.name}
                        </option>
                      ))
                    )}
                  </select>
                </div>
                {errors.city && <div className={styles.errorMsg}>{errors.city}</div>}
              </div>
            </div>
            {/* PAN & Account Number */}
            <div className={styles.row}>
              <div className={styles.inputGroup}>
                <label>PAN</label>
                <div className={styles.inputWithIcon}>
                  <FiCreditCard className={styles.icon} />
                  <input
                    type="text"
                    name="pan"
                    value={form.pan}
                    onChange={handleChange}
                    placeholder="e.g. ABCDE1234F"
                    maxLength={10}
                    style={errors.pan ? { borderColor: "#e43e2d" } : {}}
                  />
                </div>
                {errors.pan && <div className={styles.errorMsg}>{errors.pan}</div>}
              </div>
              <div className={styles.inputGroup}>
                <label>Account Number</label>
                <div className={styles.inputWithIcon}>
                  <FiHash className={styles.icon} />
                  <input
                    type="text"
                    name="account_number"
                    value={form.account_number}
                    onChange={handleChange}
                    placeholder="e.g. 123456789012"
                  />
                </div>
              </div>
            </div>
            {/* Password */}
            <div className={styles.inputGroup}>
              <label>Password</label>
              <div className={styles.inputWithIcon}>
                <FiKey className={styles.icon} />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Set a password"
                  autoComplete="new-password"
                  style={errors.password ? { borderColor: "#e43e2d" } : {}}
                />
                <span
                  className={styles.eyeIcon}
                  onClick={handleTogglePassword}
                  tabIndex={0}
                  role="button"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>
              {errors.password && <div className={styles.errorMsg}>{errors.password}</div>}
            </div>
            {errors.form && <div className={styles.errorMsg}>{errors.form}</div>}
            <div className={styles.footer}>
              <button type="submit" className={styles.saveBtn} disabled={loading}>
                {loading
                  ? editEmployee
                    ? "Updating..."
                    : "Saving..."
                  : editEmployee
                    ? "Update Employee"
                    : "Save & Upload Documents"}
              </button>
              <button type="button" className={styles.cancelBtn} onClick={onClose}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
      {/* Document Upload */}
      {showDocPopup && newEmployeeId && !editEmployee && (
        <DocUploadPopup
          open={showDocPopup}
          employeeId={newEmployeeId}
          onClose={handleDocPopupClose}
        />
      )}
    </>
  );
};

export default AddEmployeePanel;
